/**
 * Input sampling and batching.
 *
 * Produces exactly one `InputFrame` per simulation tick and ships them in small
 * batches. What an InputFrame contains is the whole security model of this
 * project: a tick, a sequence number, a button bitfield, a movement intent in
 * [-1,1], and a yaw. THERE IS NO POSITION IN IT, and there is nowhere in this
 * file that could put one there.
 *
 * The server re-sanitizes everything anyway (`sanitizeInputFrame`), so this is
 * not a trust boundary — it is a correctness boundary. Sending a clean frame
 * means prediction and authority run the same numbers, which is what stops the
 * local body from rubber-banding.
 */

import { Button, sanitizeInputFrame } from '@chaos/protocol';
import { JOYSTICK_DEADZONE, MAX_PENDING_INPUTS } from '../config.js';

// `Button` is a plain `enum`, which TypeScript emits as a real object. It was a
// `const enum` until integration: a const enum lands in the .d.ts as an AMBIENT
// const enum, which no consumer with `verbatimModuleSyntax` can read at all —
// `packages/sim` failed to build against protocol's dist/ because of it. This
// guard predates that fix and still earns its place: if the enum ever goes back
// to `const`, it gets inlined at compile time, vanishes from the JS build, and
// every InputFrame this client sends silently carries `buttons: NaN`. A player
// whose abilities stopped working with no error is a bug report nobody can
// action, so it fails here instead.
if (typeof Button?.Jump !== 'number') {
  throw new Error('@chaos/protocol did not emit Button as a runtime value');
}

export class InputSender {
  constructor(connection) {
    this.connection = connection;
    this.seq = 0;
    /** Frames sent but not yet acknowledged. Replayed on reconciliation. */
    this.pending = [];
    this._outbox = [];
  }

  /**
   * Builds the frame for one tick.
   *
   * `intent` is `{ moveX, moveZ }` already in the player's own reference frame
   * (forward is +Z of the camera yaw), because that is the frame the sim rotates
   * by the player's yaw. `buttons` is the raw action state for this tick.
   */
  sample(tick, yaw, intent, actions) {
    let moveX = intent.moveX;
    let moveZ = intent.moveZ;

    // A resting thumb must not walk. Below the deadzone the intent is zero, not
    // small: the sim treats any magnitude over 0.01 as "has input" and would
    // otherwise accelerate toward WALK_SPEED forever.
    const mag = Math.hypot(moveX, moveZ);
    if (mag < JOYSTICK_DEADZONE) {
      moveX = 0;
      moveZ = 0;
    } else if (mag > 1) {
      moveX /= mag;
      moveZ /= mag;
    }

    let buttons = 0;
    if (actions.jump) buttons |= Button.Jump;
    if (actions.dash) buttons |= Button.Dash;
    if (actions.basic) buttons |= Button.Basic;
    if (actions.skill1) buttons |= Button.Skill1;
    if (actions.skill2) buttons |= Button.Skill2;
    if (actions.ultimate) buttons |= Button.Ultimate;

    this.seq += 1;
    const frame = sanitizeInputFrame({
      tick,
      seq: this.seq,
      buttons,
      moveX,
      moveZ,
      yaw,
    });

    // sanitizeInputFrame only returns null on a non-finite number, which would
    // mean a NaN leaked out of the camera or the stick. Coasting for one tick is
    // strictly better than shipping a frame the server will reject.
    if (!frame) return null;

    this.pending.push(frame);
    this._outbox.push(frame);

    // A pathological backlog means the socket is gone. Dropping the oldest keeps
    // replay bounded; the snapshot that eventually arrives resnaps us anyway.
    if (this.pending.length > MAX_PENDING_INPUTS) {
      this.pending.splice(0, this.pending.length - MAX_PENDING_INPUTS);
    }
    return frame;
  }

  /** Ships whatever has accumulated. Called on a timer, not per tick. */
  flush() {
    if (this._outbox.length === 0) return;
    this.connection.sendInput(this._outbox);
    this._outbox = [];
  }

  /** Drops frames the server has confirmed it processed. */
  ack(lastProcessedSeq) {
    if (this.pending.length === 0) return;
    let i = 0;
    while (i < this.pending.length && this.pending[i].seq <= lastProcessedSeq) i++;
    if (i > 0) this.pending.splice(0, i);
  }

  /** Frames the server has not confirmed yet, oldest first. Replay order. */
  unacked() {
    return this.pending;
  }

  reset() {
    this.pending.length = 0;
    this._outbox.length = 0;
  }
}
